import React from 'react';
import { cn } from '@/lib/utils';

export interface ProgressProps extends React.HTMLAttributes<HTMLDivElement> {
  value?: number;
  max?: number;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'success' | 'warning' | 'error';
  showLabel?: boolean;
  label?: string;
}

const sizeClasses = {
  sm: 'h-1',
  md: 'h-2',
  lg: 'h-3',
};

const variantClasses = {
  default: 'bg-primary-600',
  success: 'bg-success-600',
  warning: 'bg-warning-600',
  error: 'bg-error-600',
};

export function Progress({
  value = 0,
  max = 100,
  size = 'md',
  variant = 'default',
  showLabel = false,
  label,
  className,
  ...props
}: ProgressProps) {
  const percentage = Math.min(Math.max((value / max) * 100, 0), 100);
  const displayLabel = label || `${Math.round(percentage)}%`;

  return (
    <div className={cn('w-full', className)} {...props}>
      {showLabel && (
        <div className="flex justify-between mb-1">
          <span className="text-sm font-medium text-foreground">{displayLabel}</span>
          <span className="text-sm text-muted-foreground">{Math.round(percentage)}%</span>
        </div>
      )}
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={value}
        /**
         * W2-I a11y fix: progressbar must always have an accessible name.
         * Fall back to the percentage string when no label is provided so
         * screen readers can announce the progress value.
         */
        aria-label={label || `${Math.round(percentage)}%`}
        aria-valuetext={`${Math.round(percentage)}%`}
        className={cn(
          'w-full overflow-hidden rounded-full bg-muted',
          sizeClasses[size]
        )}
      >
        <div
          className={cn(
            'h-full transition-all duration-300 ease-in-out rounded-full',
            variantClasses[variant]
          )}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

// Circular progress variant
export interface CircularProgressProps extends React.SVGProps<SVGSVGElement> {
  value?: number;
  max?: number;
  size?: number | 'sm' | 'md' | 'lg';
  strokeWidth?: number;
  variant?: 'default' | 'success' | 'warning' | 'error';
  showLabel?: boolean;
}

const sizeMap = {
  sm: 48,
  md: 64,
  lg: 80,
};

export function CircularProgress({
  value = 0,
  max = 100,
  size = 'md',
  strokeWidth = 8,
  variant = 'default',
  showLabel = false,
  className,
  ...props
}: CircularProgressProps) {
  const actualSize = typeof size === 'string' ? sizeMap[size] : size;
  const percentage = Math.min(Math.max((value / max) * 100, 0), 100);
  const radius = (actualSize - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;

  const strokeColor = {
    default: 'stroke-primary-600',
    success: 'stroke-success-600',
    warning: 'stroke-warning-600',
    error: 'stroke-error-600',
  };

  return (
    <div className="relative inline-flex">
      <svg
        width={actualSize}
        height={actualSize}
        className={cn('transform -rotate-90', className)}
        {...props}
      >
        <circle
          cx={actualSize / 2}
          cy={actualSize / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="none"
          className="text-muted"
        />
        <circle
          cx={actualSize / 2}
          cy={actualSize / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className={cn('transition-all duration-300 ease-in-out', strokeColor[variant])}
        />
      </svg>
      {showLabel && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-lg font-semibold">{Math.round(percentage)}%</span>
        </div>
      )}
    </div>
  );
}
